<template>
    <div class="card h-100">
        <!-- Card Header -->
        <div class="card-header">
            <div class="header-icon">
                <i class="fa-solid fa-chart-pie"></i>
            </div>
            <div class="header-content">
                <h3 class="card-title">Category Spending Limits</h3>
                <p class="card-subtitle">Set monthly budgets for each category</p>
            </div>
        </div>

        <!-- Card Body -->
        <div class="card-body ps-3 pe-3 pb-3">
            <!-- Add Category Button -->
            <div class="add-category-section mb-3">
                <button @click="startAddingCategory" class="btn btn-primary btn-lg w-100">
                    <i class="fa-solid fa-plus me-2"></i>Add New Category
                </button>
            </div>

            <!-- Categories List -->
            <div class="categories-list">
                <!-- Empty State -->
                <div v-if="categories.length === 0" class="text-muted text-center py-3">
                    <i class="fa-solid fa-circle-info me-1"></i>No categories found. Add a category above to get started.
                </div>

                <div v-else>
                    <!-- Categories with Limits Section -->
                    <div v-if="categoriesWithLimits.length > 0" class="limited-categories-section mb-3">
                        <label class="form-label fw-semibold">
                            <i class="fa-solid fa-chart-pie me-1"></i>Limited Categories ({{ categoriesWithLimits.length }})
                        </label>
                        
                        <div class="category-items">
                            <div v-for="category in categoriesWithLimits" :key="category" class="category-item">
                                <div class="row g-0">
                                    <!-- Main content row -->
                                    <div class="col">
                                        <div class="category-main-row d-flex justify-content-between align-items-center">
                                            <div class="category-info flex-grow-1">
                                                <span class="category-name">{{ category }}</span>
                                                <!-- Progress Bar - Always show for limited categories -->
                                                <div class="progress-section mt-2 pe-3">
                                                    <div class="progress mb-2" style="height: 40px; border-radius: 20px;">
                                                        <div class="progress-bar progress-bar-striped" :class="{
                                                            'bg-success': !isOverLimit(category),
                                                            'bg-danger': isOverLimit(category)
                                                        }" role="progressbar" :style="{
                                                            width: Math.min(getSpendingPercentage(category), 100) + '%',
                                                            borderRadius: '20px'
                                                        }" :aria-valuenow="getSpendingPercentage(category)" aria-valuemin="0"
                                                            aria-valuemax="100"
                                                            :aria-label="`${getSpendingPercentage(category).toFixed(0)}% of spending limit used`">
                                                        </div>
                                                    </div>

                                                    <div class="d-flex justify-content-between align-items-center ps-3 pe-3">
                                                        <small class="fw-bold">
                                                            <i class="fa-solid fa-dollar-sign me-1"></i>
                                                            ${{ getCurrentSpending(category).toFixed(2) }} / ${{ getLimit(category).toFixed(2) }}
                                                        </small>
                                                        <small class="fw-bold" :class="{
                                                            'text-success': !isOverLimit(category),
                                                            'text-danger': isOverLimit(category)
                                                        }">
                                                            {{ getSpendingPercentage(category).toFixed(0) }}%
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>

                                            <div class="category-actions">
                                                <!-- Edit Limit Button -->
                                                <button @click="startEditing(category)" class="btn delete-btn" title="Edit limit">
                                                    <i class="fa-solid fa-edit"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Overage Display - Full width row below -->
                                <div v-if="isOverLimit(category)" class="row g-0 mt-3">
                                    <div class="col">
                                        <div class="alert alert-danger py-2 px-3 mb-0">
                                            <small class="d-flex align-items-center fw-bold">
                                                <i class="fa-solid fa-exclamation-triangle me-2"></i>
                                                ${{ getOverageAmount(category).toFixed(2) }} over limit
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Categories without Limits Section -->
                    <div v-if="categoriesWithoutLimits.length > 0" class="unlimited-categories-section">
                        <!-- Expandable Header -->
                        <button 
                            @click="showUnlimitedCategories = !showUnlimitedCategories"
                            class="expand-button w-100 d-flex justify-content-between align-items-center"
                        >
                            <label class="form-label fw-semibold mb-0">
                                <i class="fa-solid fa-tags me-1"></i>Other Categories ({{ categoriesWithoutLimits.length }})
                            </label>
                            <i class="fa-solid" :class="showUnlimitedCategories ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                        </button>

                        <!-- Expandable Content -->
                        <div v-show="showUnlimitedCategories" class="category-items mt-2">
                            <div v-for="category in categoriesWithoutLimits" :key="category" class="category-item">
                                <div class="row g-0">
                                    <!-- Main content row -->
                                    <div class="col">
                                        <div class="category-main-row d-flex justify-content-between align-items-center">
                                            <div class="category-info flex-grow-1">
                                                <span class="category-name">{{ category }}</span>
                                            </div>

                                            <div class="category-actions">
                                                <!-- Set Limit Button -->
                                                <button @click="startEditing(category)" class="btn delete-btn" title="Set limit">
                                                    <i class="fa-solid fa-plus"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Success/Error Messages -->
            <div v-if="feedbackMessage" class="feedback-message mt-3" :class="feedbackType">
                <i class="fa-solid"
                    :class="feedbackType === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'"></i>
                {{ feedbackMessage }}
            </div>
        </div>

        <!-- Spending Limit Modal -->
        <div v-if="showModal" class="modal-overlay" @click.self="cancelEditing">
            <div class="modal-content">
                <div class="modal-header">
                    <h4 class="modal-title">
                        {{ hasLimit(editingCategory) ? 'Edit' : 'Set' }} Spending Limit
                    </h4>
                    <button type="button" class="btn-close" @click="cancelEditing" aria-label="Close">
                        <i class="bi bi-x-lg"></i>
                    </button>
                </div>

                <div class="modal-body">
                    <div class="mb-3">
                        <label class="form-label fw-semibold">
                            <i class="fa-solid fa-tag me-1"></i>Category
                        </label>
                        <div class="category-display">
                            <span class="category-name-modal">{{ editingCategory }}</span>
                        </div>
                    </div>

                    <div class="mb-2">
                        <label for="spendingLimitAmount" class="form-label fw-semibold">
                            <i class="fa-solid fa-dollar-sign me-1"></i>Monthly Spending Limit
                        </label>
                        <div class="input-group input-group-lg">
                            <span class="input-group-text currency-symbol">$</span>
                            <input ref="modalLimitInput" id="spendingLimitAmount" v-model="editingAmount"
                                @keyup.enter="saveLimit" @keyup.escape="cancelEditing" type="number" step="0.01"
                                min="0.01" class="form-control form-control-lg"
                                :class="{ 'is-invalid': validationError }" placeholder="0.00" />
                        </div>
                        <div v-if="validationError" class="invalid-feedback d-block">
                            {{ validationError }}
                        </div>
                        <div class="form-text">
                            Enter the maximum amount you want to spend in this category per month.
                        </div>
                    </div>
                </div>

                <div class="modal-footer" :class="{ 'single-button': !hasLimit(editingCategory) }">
                    <button v-if="hasLimit(editingCategory)" @click="confirmRemoveLimit(editingCategory)"
                        class="btn btn-outline-danger btn-lg" title="Remove spending limit">
                        <i class="bi bi-trash-fill me-1"></i>Remove Limit
                    </button>
                    <button @click="saveLimit" class="btn btn-primary btn-lg" :disabled="!isValidAmount">
                        {{ hasLimit(editingCategory) ? 'Update' : 'Set' }} Limit
                    </button>
                </div>
            </div>
        </div>

        <!-- Add Category Modal -->
        <div v-if="showAddCategoryModal" class="modal-overlay" @click.self="cancelAddingCategory">
            <div class="modal-content">
                <div class="modal-header">
                    <h4 class="modal-title">Add New Category</h4>
                    <button type="button" class="btn-close" @click="cancelAddingCategory" aria-label="Close">
                        <i class="bi bi-x-lg"></i>
                    </button>
                </div>

                <div class="modal-body">
                    <div class="mb-3">
                        <label for="newCategoryName" class="form-label fw-semibold">
                            <i class="fa-solid fa-tag me-1"></i>Category Name
                        </label>
                        <input 
                            ref="modalCategoryInput" 
                            id="newCategoryName" 
                            v-model="newCategoryName"
                            @keyup.enter="saveNewCategory" 
                            @keyup.escape="cancelAddingCategory" 
                            type="text" 
                            class="form-control form-control-lg"
                            :class="{ 'is-invalid': newCategoryValidationError }" 
                            placeholder="Enter category name"
                            autocomplete="on"
                        />
                        <div v-if="newCategoryValidationError" class="invalid-feedback d-block">
                            {{ newCategoryValidationError }}
                        </div>
                    </div>

                    <div class="mb-2">
                        <label for="newCategoryLimit" class="form-label fw-semibold">
                            <i class="fa-solid fa-dollar-sign me-1"></i>Monthly Spending Limit
                        </label>
                        <div class="input-group input-group-lg">
                            <span class="input-group-text currency-symbol">$</span>
                            <input 
                                ref="modalLimitInput" 
                                id="newCategoryLimit" 
                                v-model="newCategoryLimit"
                                @keyup.enter="saveNewCategory" 
                                @keyup.escape="cancelAddingCategory" 
                                type="number" 
                                step="0.01"
                                min="0.01" 
                                class="form-control form-control-lg"
                                :class="{ 'is-invalid': newCategoryLimitError }" 
                                placeholder="0.00"
                            />
                        </div>
                        <div v-if="newCategoryLimitError" class="invalid-feedback d-block">
                            {{ newCategoryLimitError }}
                        </div>
                        <div class="form-text">
                            Enter the maximum amount you want to spend in this category per month.
                        </div>
                    </div>
                </div>

                <div class="modal-footer single-button">
                    <button @click="saveNewCategory" class="btn btn-primary btn-lg" :disabled="!isValidNewCategory">
                        <i class="fa-solid fa-plus me-1"></i>Add Category with Limit
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useCategorySpendingLimits } from '../composables/useCategorySpendingLimits.js';
import { useErrorModal } from '../composables/useErrorModal.js';

export default {
    name: 'CategorySpendingLimits',
    props: {
        transactions: {
            type: Array,
            required: true,
            default: () => []
        }
    },
    data() {
        return {
            showModal: false,
            editingCategory: null,
            editingAmount: '',
            validationError: '',
            feedbackMessage: '',
            feedbackType: 'success', // 'success' or 'error'
            feedbackTimeout: null,
            _spendingCache: null, // Cache for current month spending calculations
            showUnlimitedCategories: false, // Track expanded state for categories without limits
            showAddCategoryModal: false,
            newCategoryName: '',
            newCategoryValidationError: '',
            newCategoryLimit: '',
            newCategoryLimitError: ''
        };
    },
    setup() {
        const {
            getLimit,
            hasLimit,
            setLimit,
            removeLimit,
            getCategoriesWithLimits
        } = useCategorySpendingLimits();

        const { showApiError } = useErrorModal();

        return {
            getLimit,
            hasLimit,
            setLimit,
            removeLimit,
            getCategoriesWithLimits,
            showApiError
        };
    },
    computed: {
        // Extract unique categories from transactions and existing spending limits
        categories() {
            const uniqueCategories = new Set();

            // Add categories from transactions
            this.transactions.forEach(transaction => {
                if (transaction.category && transaction.category.trim()) {
                    uniqueCategories.add(transaction.category.trim());
                }
            });

            // Add categories that have existing spending limits
            this.getCategoriesWithLimits().forEach(category => {
                uniqueCategories.add(category);
            });

            return Array.from(uniqueCategories).sort((a, b) =>
                a.toLowerCase().localeCompare(b.toLowerCase())
            );
        },

        // Categories with spending limits (sorted to top)
        categoriesWithLimits() {
            return this.categories.filter(category => this.hasLimit(category));
        },

        // Categories without spending limits
        categoriesWithoutLimits() {
            return this.categories.filter(category => !this.hasLimit(category));
        },

        // Calculate current month spending per category with memoization
        currentMonthSpending() {
            const now = new Date();
            const currentMonth = now.getMonth();
            const currentYear = now.getFullYear();

            // Create a cache key based on transactions length and current month/year
            const cacheKey = `${this.transactions.length}-${currentMonth}-${currentYear}`;

            // Use memoization to avoid recalculation on every render
            if (this._spendingCache && this._spendingCache.key === cacheKey) {
                return this._spendingCache.data;
            }

            const spending = {};

            // Filter transactions for current month debit transactions only
            const currentMonthDebits = this.transactions.filter(transaction => {
                // Handle edge cases: missing dates or invalid transaction data
                if (!transaction.date || transaction.type !== 'debit') {
                    return false;
                }

                try {
                    const transactionDate = new Date(transaction.date);

                    // Check if date is valid
                    if (isNaN(transactionDate.getTime())) {
                        return false;
                    }

                    return transactionDate.getMonth() === currentMonth &&
                        transactionDate.getFullYear() === currentYear;
                } catch (error) {
                    // Handle invalid date parsing
                    return false;
                }
            });

            // Calculate spending per category
            currentMonthDebits.forEach(transaction => {
                if (transaction.category && transaction.category.trim()) {
                    const category = transaction.category.trim();
                    const amount = parseFloat(transaction.amount);

                    // Handle invalid amounts
                    if (!isNaN(amount) && isFinite(amount)) {
                        spending[category] = (spending[category] || 0) + Math.abs(amount);
                    }
                }
            });

            // Cache the result for performance optimization
            this._spendingCache = {
                key: cacheKey,
                data: spending
            };

            return spending;
        },

        // Validate the current editing amount
        isValidAmount() {
            if (!this.editingAmount) return false;

            const amount = parseFloat(this.editingAmount);
            return !isNaN(amount) && amount > 0 && isFinite(amount);
        },

        // Validate the new category name and limit
        isValidNewCategory() {
            return this.isValidCategoryName && this.isValidCategoryLimit;
        },

        // Validate the new category name
        isValidCategoryName() {
            if (!this.newCategoryName) return false;
            
            const trimmed = this.newCategoryName.trim();
            return trimmed.length > 0 && trimmed.length <= 50;
        },

        // Validate the new category limit
        isValidCategoryLimit() {
            if (!this.newCategoryLimit) return false;

            const amount = parseFloat(this.newCategoryLimit);
            return !isNaN(amount) && amount > 0 && isFinite(amount);
        }
    },
    methods: {
        // Get current month spending for a specific category
        getCurrentSpending(category) {
            if (!category || typeof category !== 'string') {
                return 0;
            }

            return this.currentMonthSpending[category.trim()] || 0;
        },

        // Calculate spending percentage against limit
        getSpendingPercentage(category) {
            if (!this.hasLimit(category)) {
                return 0;
            }

            const currentSpending = this.getCurrentSpending(category);
            const limit = this.getLimit(category);

            if (!limit || limit <= 0) {
                return 0;
            }

            return Math.min((currentSpending / limit) * 100, 100);
        },

        // Check if spending is over the limit
        isOverLimit(category) {
            if (!this.hasLimit(category)) {
                return false;
            }

            const currentSpending = this.getCurrentSpending(category);
            const limit = this.getLimit(category);

            return currentSpending > limit;
        },

        // Get overage amount (amount over the limit)
        getOverageAmount(category) {
            if (!this.isOverLimit(category)) {
                return 0;
            }

            const currentSpending = this.getCurrentSpending(category);
            const limit = this.getLimit(category);

            return currentSpending - limit;
        },

        // Start editing a category's spending limit
        startEditing(category) {
            this.editingCategory = category;
            this.editingAmount = this.hasLimit(category) ? this.getLimit(category).toString() : '';
            this.validationError = '';
            this.showModal = true;
            this.clearFeedback();

            // Focus the input after Vue updates the DOM
            this.$nextTick(() => {
                if (this.$refs.modalLimitInput) {
                    this.$refs.modalLimitInput.focus();
                    this.$refs.modalLimitInput.select();
                }
            });
        },

        // Cancel editing
        cancelEditing() {
            this.showModal = false;
            this.editingCategory = null;
            this.editingAmount = '';
            this.validationError = '';
        },

        // Validate the amount and set error message
        validateAmount() {
            if (!this.editingAmount) {
                this.validationError = 'Please enter an amount';
                return false;
            }

            const amount = parseFloat(this.editingAmount);

            if (isNaN(amount)) {
                this.validationError = 'Please enter a valid number';
                return false;
            }

            if (amount <= 0) {
                this.validationError = 'Amount must be greater than 0';
                return false;
            }

            if (!isFinite(amount)) {
                this.validationError = 'Please enter a reasonable amount';
                return false;
            }

            this.validationError = '';
            return true;
        },

        // Save the spending limit
        async saveLimit() {
            if (!this.validateAmount()) {
                return;
            }

            const category = this.editingCategory;
            const amount = parseFloat(this.editingAmount);

            try {
                await this.setLimit(category, amount);
                this.showFeedback(`Spending limit set for ${category}: $${amount.toFixed(2)}`, 'success');
                this.cancelEditing();
            } catch (error) {
                console.error('Error saving spending limit:', error);
                this.showApiError(error);
            }
        },

        // Confirm and remove spending limit
        async confirmRemoveLimit(category) {
            if (confirm(`Remove spending limit for "${category}"?`)) {
                try {
                    await this.removeLimit(category);
                    this.showFeedback(`Spending limit removed for ${category}`, 'success');
                    this.cancelEditing(); // Close modal after removing limit
                } catch (error) {
                    console.error('Error removing spending limit:', error);
                    this.showApiError(error);
                }
            }
        },

        // Show feedback message with auto-hide
        showFeedback(message, type = 'success') {
            this.feedbackMessage = message;
            this.feedbackType = type;

            // Clear any existing timeout
            if (this.feedbackTimeout) {
                clearTimeout(this.feedbackTimeout);
            }

            // Auto-hide after 3 seconds
            this.feedbackTimeout = setTimeout(() => {
                this.clearFeedback();
            }, 3000);
        },

        // Clear feedback message
        clearFeedback() {
            this.feedbackMessage = '';
            if (this.feedbackTimeout) {
                clearTimeout(this.feedbackTimeout);
                this.feedbackTimeout = null;
            }
        },

        // Start adding a new category
        startAddingCategory() {
            this.newCategoryName = '';
            this.newCategoryValidationError = '';
            this.newCategoryLimit = '';
            this.newCategoryLimitError = '';
            this.showAddCategoryModal = true;
            this.clearFeedback();

            // Focus the category name input after Vue updates the DOM
            this.$nextTick(() => {
                if (this.$refs.modalCategoryInput) {
                    this.$refs.modalCategoryInput.focus();
                }
            });
        },

        // Cancel adding a new category
        cancelAddingCategory() {
            this.showAddCategoryModal = false;
            this.newCategoryName = '';
            this.newCategoryValidationError = '';
            this.newCategoryLimit = '';
            this.newCategoryLimitError = '';
        },

        // Validate the category name and set error message
        validateCategoryName() {
            if (!this.newCategoryName) {
                this.newCategoryValidationError = 'Please enter a category name';
                return false;
            }

            const trimmed = this.newCategoryName.trim();

            if (trimmed.length === 0) {
                this.newCategoryValidationError = 'Category name cannot be empty';
                return false;
            }

            if (trimmed.length > 50) {
                this.newCategoryValidationError = 'Category name must be 50 characters or less';
                return false;
            }

            // Check if category already exists (case-insensitive)
            const normalizedName = trimmed.toLowerCase();
            const exists = this.categories.some(cat => cat.toLowerCase() === normalizedName);
            
            if (exists) {
                this.newCategoryValidationError = 'Category already exists';
                return false;
            }

            this.newCategoryValidationError = '';
            return true;
        },

        // Validate the category limit and set error message
        validateCategoryLimit() {
            if (!this.newCategoryLimit) {
                this.newCategoryLimitError = 'Please enter a spending limit';
                return false;
            }

            const amount = parseFloat(this.newCategoryLimit);

            if (isNaN(amount)) {
                this.newCategoryLimitError = 'Please enter a valid number';
                return false;
            }

            if (amount <= 0) {
                this.newCategoryLimitError = 'Amount must be greater than 0';
                return false;
            }

            if (!isFinite(amount)) {
                this.newCategoryLimitError = 'Please enter a reasonable amount';
                return false;
            }

            this.newCategoryLimitError = '';
            return true;
        },

        // Save the new category with spending limit
        async saveNewCategory() {
            const isValidName = this.validateCategoryName();
            const isValidLimit = this.validateCategoryLimit();

            if (!isValidName || !isValidLimit) {
                return;
            }

            const categoryName = this.newCategoryName.trim();
            const amount = parseFloat(this.newCategoryLimit);

            try {
                // Set the spending limit for the new category (this creates the category)
                await this.setLimit(categoryName, amount);
                this.showFeedback(`Category "${categoryName}" added with $${amount.toFixed(2)} spending limit`, 'success');
                this.cancelAddingCategory();
            } catch (error) {
                console.error('Error adding new category:', error);
                this.showApiError(error);
            }
        }
    },

    watch: {
        // Invalidate spending cache when transactions change
        transactions: {
            handler() {
                this._spendingCache = null;
            },
            deep: true
        }
    },

    beforeUnmount() {
        // Clean up timeout
        if (this.feedbackTimeout) {
            clearTimeout(this.feedbackTimeout);
        }
    }
};
</script>

<style scoped>
.card {
    border-radius: 5px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    overflow: hidden;
}

.card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--light-border);
    margin-bottom: 1rem;
}

.header-content {
    flex: 1;
    min-width: 0;
}

.card-title {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--light-text);
    line-height: 1.2;
}

.card-subtitle {
    margin: 0;
    font-size: 0.85rem;
    color: var(--light-text-muted);
    line-height: 1.3;
}

.card-body {
    padding: 0;
}

.add-category-section {
    padding: 0 12px;
}

.limited-categories-section {
    margin-bottom: 1rem;
}

.unlimited-categories-section {
    margin-top: 1rem;
}

.expand-button {
    background: var(--nested-container-bg);
    border: none;
    border-radius: 8px;
    padding: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    color: var(--light-text);
}

.expand-button:hover {
    background: rgba(0, 0, 0, 0.04);
}

.expand-button .form-label {
    cursor: pointer;
    color: var(--light-text);
}

.category-items {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.category-item {
    background: var(--nested-container-bg);
    border-radius: 8px;
    transition: background 0.2s ease;
    padding: 12px;
}

.category-item:hover {
    background: rgba(0, 0, 0, 0.04);
}

.category-main-row {
    min-height: 44px; /* Ensure adequate touch target height */
}

.category-info {
    display: flex;
    flex-direction: column;
    flex: 1;
    font-size: 14px;
    gap: 4px;
}

.category-name {
    font-weight: 600;
    color: var(--light-text);
}

.limit-display {
    display: flex;
    align-items: center;
    gap: 6px;
}

.limit-amount {
    font-weight: 600;
    color: var(--light-success);
    font-size: 13px;
}

.limit-label {
    font-size: 12px;
    color: var(--light-text-muted);
}

.category-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.progress-section {
    width: 100%;
}

.feedback-message {
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.feedback-message.success {
    background-color: rgba(40, 167, 69, 0.1);
    color: var(--light-success);
    border: 1px solid rgba(40, 167, 69, 0.2);
}

.feedback-message.error {
    background-color: rgba(220, 53, 69, 0.1);
    color: var(--light-danger);
    border: 1px solid rgba(220, 53, 69, 0.2);
}

/* Dark mode */
:global([data-theme]) .card-title {
    color: var(--light-text);
}

:global([data-theme]) .card-subtitle {
    color: var(--light-text-muted);
}

:global([data-theme]) .category-item:hover {
    background: rgba(255, 255, 255, 0.08);
}

:global([data-theme]) .category-name {
    color: var(--light-text);
}

:global([data-theme]) .limit-amount {
    color: var(--light-success);
}

:global([data-theme]) .limit-label {
    color: var(--light-text-muted);
}

:global([data-theme]) .feedback-message.success {
    background-color: rgba(40, 167, 69, 0.15);
    color: var(--light-success);
}

:global([data-theme]) .feedback-message.error {
    background-color: rgba(220, 53, 69, 0.15);
    color: var(--light-danger);
}

:global([data-theme]) .expand-button {
    color: var(--light-text);
}

:global([data-theme]) .expand-button:hover {
    background: rgba(255, 255, 255, 0.08);
}

:global([data-theme]) .expand-button .form-label {
    color: var(--light-text);
}



/* Modal Styles */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
    padding: 1rem;
}

.modal-content {
    background-color: var(--light-card);
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
    max-width: 500px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.5rem 1.5rem 1rem;
    border-bottom: 1px solid var(--light-border);
}

.modal-title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--light-text);
}

.btn-close {
    background: none;
    border: none;
    font-size: 1.25rem;
    color: var(--light-text-muted);
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 6px;
    transition: all 0.2s ease;
}

.btn-close:hover {
    background-color: rgba(0, 0, 0, 0.05);
    color: var(--light-text);
}

.modal-body {
    padding: 1.5rem;
}

.modal-footer {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 1rem 1.5rem 1.5rem;
    border-top: 1px solid var(--light-border);
}

.modal-footer .btn {
    flex: 1;
}

.modal-footer.single-button {
    justify-content: center;
}

.modal-footer.single-button .btn {
    flex: 0 0 auto;
    min-width: 200px;
}

.category-display {
    padding: 12px;
    background: var(--nested-container-bg);
    border-radius: 8px;
    border: 1px solid var(--light-border);
}

.category-name-modal {
    font-weight: 600;
    color: var(--light-text);
    font-size: 1rem;
}

/* Dark mode modal styles */
:global([data-theme]) .modal-overlay {
    background-color: rgba(0, 0, 0, 0.8);
}

:global([data-theme]) .modal-content {
    background-color: var(--light-card);
}

:global([data-theme]) .modal-title {
    color: var(--light-text);
}

:global([data-theme]) .btn-close {
    color: var(--light-text-muted);
}

:global([data-theme]) .btn-close:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: var(--light-text);
}

:global([data-theme]) .category-name-modal {
    color: var(--light-text);
}

@media (max-width: 768px) {
    .card-title {
        font-size: 1rem;
    }

    .card-subtitle {
        font-size: 0.8rem;
    }

    .category-info {
        font-size: 13px;
    }

    .modal-overlay {
        padding: 0.5rem;
    }

    .modal-header,
    .modal-body,
    .modal-footer {
        padding: 1rem;
    }

    .modal-title {
        font-size: 1.1rem;
    }
}
</style>